import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Backdrop,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { apiClient } from '../../api/apiClient';
import FormatBold from '@mui/icons-material/FormatBold';
import FormatItalic from '@mui/icons-material/FormatItalic';
import FormatUnderlined from '@mui/icons-material/FormatUnderlined';
import FormatListBulleted from '@mui/icons-material/FormatListBulleted';
import FormatListNumbered from '@mui/icons-material/FormatListNumbered';
import FormatClear from '@mui/icons-material/FormatClear';

const SimpleMenuBar = ({ editor }) => {
  if (!editor) return null;
  const handleClick = (callback) => (e) => { e.preventDefault(); callback(); };
  return (
    <ToggleButtonGroup size="small" sx={{ mb: 1 }}>
      <ToggleButton value="bold" selected={editor.isActive('bold')} onMouseDown={handleClick(() => editor.chain().focus().toggleBold().run())}><FormatBold /></ToggleButton>
      <ToggleButton value="italic" selected={editor.isActive('italic')} onMouseDown={handleClick(() => editor.chain().focus().toggleItalic().run())}><FormatItalic /></ToggleButton>
      <ToggleButton value="underline" selected={editor.isActive('underline')} onMouseDown={handleClick(() => editor.chain().focus().toggleUnderline().run())}><FormatUnderlined /></ToggleButton>
      <ToggleButton value="bulletList" selected={editor.isActive('bulletList')} onMouseDown={handleClick(() => editor.chain().focus().toggleBulletList().run())}><FormatListBulleted /></ToggleButton>
      <ToggleButton value="orderedList" selected={editor.isActive('orderedList')} onMouseDown={handleClick(() => editor.chain().focus().toggleOrderedList().run())}><FormatListNumbered /></ToggleButton>
      <ToggleButton value="clear" onMouseDown={handleClick(() => editor.chain().focus().clearNodes().unsetAllMarks().run())}><FormatClear /></ToggleButton>
    </ToggleButtonGroup>
  );
};

const SimpleEditor = ({ value, onChange, minHeight = 200 }) => {
  const editor = useEditor({
    extensions: [StarterKit, Underline],
    content: value,
    onUpdate: ({ editor }) => onChange(editor.getHTML())
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  return (
    <Paper variant="outlined" sx={{ p: 1 }}>
      <SimpleMenuBar editor={editor} />
      <Box sx={{ '.ProseMirror': { minHeight, p: 2, outline: 'none' } }}>
        <EditorContent editor={editor} />
      </Box>
    </Paper>
  );
};

const KATEGORIEN = [
  { code: 'Stellungnahme', name: 'Stellungnahme' },
  { code: 'Gutachten', name: 'Gutachten' }
];

const BUNDESLAENDER = [
  { code: "BWB", name: "Baden-Württemberg" },
  { code: "FSB", name: "Bayern" },
  { code: "BLN", name: "Berlin" },
  { code: "BRB", name: "Brandenburg" },
  { code: "BRE", name: "Bremen" },
  { code: "HAM", name: "Hamburg" },
  { code: "HES", name: "Hessen" },
  { code: "MVP", name: "Mecklenburg-Vorpommern" },
  { code: "NSB", name: "Niedersachsen" },
  { code: "NRW", name: "Nordrhein-Westfalen" },
  { code: "RPF", name: "Rheinland-Pfalz" },
  { code: "SAA", name: "Saarland" },
  { code: "FSS", name: "Sachsen" },
  { code: "SAN", name: "Sachsen-Anhalt" },
  { code: "SHS", name: "Schleswig-Holstein" },
  { code: "FST", name: "Thüringen" },
  { code: "BRD", name: "Bundesrepublik Deutschland" }
];

export default function BerufskundeAdd() {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [gerichte, setGerichte] = useState([]);
  const [formData, setFormData] = useState({
    aktenzeichen: '',
    anfrage: '',
    auskunft: '',
    auskunftgeber: '',
    kategorie: '',
    datum: new Date(),
    land: '',
    gericht_id: ''
  });

  useEffect(() => {
    const fetchGerichte = async () => {
      try {
        const response = await apiClient.get('/gerichte/', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setGerichte(response.data);
      } catch (error) {
        console.error('Fehler beim Laden der Gerichte:', error);
      }
    };
    fetchGerichte();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatDateForAPI = (date) => {
    return date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : null;
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({
      ...prev,
      datum: date
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const apiData = {
        ...formData,
        datum: formatDateForAPI(formData.datum)
      };

      await apiClient.post('/berufskunde/', apiData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      navigate('/admin/berufskunde');
    } catch (error) {
      console.error('Fehler beim Speichern der Berufskunde:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Neue Berufskunde erstellen
      </Typography>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                name="aktenzeichen"
                label="Aktenzeichen"
                value={formData.aktenzeichen}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Kategorie</InputLabel>
                <Select
                  name="kategorie"
                  value={formData.kategorie}
                  onChange={handleChange}
                  required
                  label="Kategorie"
                >
                  {KATEGORIEN.map((kategorie) => (
                    <MenuItem key={kategorie.code} value={kategorie.code}>
                      {kategorie.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Bundesland</InputLabel>
                <Select
                  name="land"
                  value={formData.land}
                  onChange={handleChange}
                  required
                  label="Bundesland"
                >
                  {BUNDESLAENDER.map((land) => (
                    <MenuItem key={land.code} value={land.code}>
                      {land.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Gericht</InputLabel>
                <Select
                  name="gericht_id"
                  value={formData.gericht_id}
                  onChange={handleChange}
                  label="Gericht"
                >
                  <MenuItem value="">
                    <em>Kein Gericht</em>
                  </MenuItem>
                  {gerichte.map((gericht) => (
                    <MenuItem key={gericht.id} value={gericht.id}>
                      {gericht.bezeichnung}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="auskunftgeber"
                label="Auskunftgeber"
                value={formData.auskunftgeber}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DatePicker
                label="Datum"
                value={formData.datum}
                onChange={handleDateChange}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Anfrage</Typography>
              <SimpleEditor
                value={formData.anfrage}
                onChange={(value) => setFormData(prev => ({ ...prev, anfrage: value }))}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Auskunft</Typography>
              <SimpleEditor
                value={formData.auskunft}
                onChange={(value) => setFormData(prev => ({ ...prev, auskunft: value }))}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/berufskunde')}
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Speichern
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
} 